"use client"

import { useState, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { Z } from "@/lib/z-index"
import { useAnimatedClose } from "@/lib/use-animated-close"
import { X } from "lucide-react"
import { usePostDetail } from "@/lib/hooks/use-post-detail"
import { PostDetailContent } from "./post-detail-content"
import { PublishModal } from "./publish-modal"

/**
 * PostDetailModal — full-detail overlay for a community post.
 *
 * Features:
 * - Shallow route: pushes /community/post/[id] to browser history
 * - Animated enter/exit (reuses useAnimatedClose pattern)
 * - Portal-rendered backdrop + centered panel
 */
export function PostDetailModal({
  postId,
  onClose,
}: {
  postId: string
  onClose: () => void
}) {
  const { closing, requestClose } = useAnimatedClose(onClose, 280)
  const [entered, setEntered] = useState(false)

  const { post, loading, handlers, panelStates, refreshPost } = usePostDetail(postId)

  /* ── Refresh post data after edit ── */
  const handleEditSuccess = useCallback(() => {
    refreshPost()
    panelStates.setEditModalOpen(false)
  }, [refreshPost, panelStates])

  /* ── Entrance animation ── */
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  /* ── Shallow route ── */
  useEffect(() => {
    const prevUrl = window.location.pathname
    const base = import.meta.env.BASE_URL.replace(/\/$/, "")
    window.history.pushState(null, "", `${base}/community/post/${postId}`)
    return () => {
      window.history.replaceState(null, "", prevUrl)
    }
  }, [postId])

  /* ── Transition styles ── */
  const overlayClass = closing
    ? "opacity-0 backdrop-blur-none"
    : entered
      ? "opacity-100 backdrop-blur-xl"
      : "opacity-0 backdrop-blur-none"

  const panelStyle: React.CSSProperties = {
    opacity: closing ? 0 : entered ? 1 : 0,
    transform: closing
      ? "scale(0.96) translateY(8px)"
      : entered
        ? "scale(1) translateY(0)"
        : "scale(0.92) translateY(12px)",
    filter: closing ? "blur(4px)" : entered ? "blur(0)" : "blur(6px)",
    transitionTimingFunction: closing
      ? "cubic-bezier(0.4, 0, 1, 1)"
      : "cubic-bezier(0.16, 1, 0.3, 1)",
  }

  if (typeof document === "undefined") return null

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`liquid-glass-overlay fixed inset-0 transition-all duration-300 ease-out ${overlayClass}`}
        style={{ zIndex: Z.MODAL_OVERLAY, pointerEvents: closing || !entered ? "none" : "auto" }}
        onClick={requestClose}
        aria-hidden
      />

      {/* Centered container */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: Z.MODAL_CONTENT, pointerEvents: "none" }}
      >
        <div
          className="liquid-glass-card no-hover relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl transition-all duration-300"
          style={{ ...panelStyle, pointerEvents: "auto" }}
        >
          {/* Close button — floating top-right */}
          <button
            onClick={requestClose}
            className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>

          <PostDetailContent
            post={post}
            loading={loading}
            handlers={handlers}
            panelStates={panelStates}
          />
        </div>
      </div>

      {/* Edit modal */}
      {post && (
        <PublishModal
          open={panelStates.editModalOpen}
          onClose={() => panelStates.setEditModalOpen(false)}
          editPost={post}
          onSuccess={handleEditSuccess}
        />
      )}
    </>,
    document.body
  )
}
